import { motion } from 'framer-motion';
import {
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Compass,
  PieChart,
  PiggyBank,
  Target,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  BottomSheet,
  Button,
  DropdownSelect,
  NumPad,
  type DropdownOption,
} from '../../components';
import type { BudgetModel, Profile } from '../../data/types';
import { cn } from '../../lib/cn';
import type { PayCycle } from '../../lib/payPeriod';
import { springQuick, tapScale } from '../../tokens';

// ---------- Edit name ----------

export function EditNameSheet({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: string;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(current);

  useEffect(() => {
    if (open) setName(current);
  }, [open, current]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Your name">
      <div className="px-card-pad py-4 flex flex-col gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder="First name"
          autoFocus
          autoCapitalize="words"
          maxLength={40}
          className="w-full h-12 px-4 rounded-button bg-surface-secondary text-[18px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
        <Button variant="primary" size="lg" fullWidth onClick={submit}>
          Save
        </Button>
      </div>
    </BottomSheet>
  );
}

// ---------- Edit hourly rate ----------

export function EditHourlyRateSheet({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: number;
  onClose: () => void;
  onSave: (amount: number) => void;
}) {
  return (
    <BottomSheet open={open} onClose={onClose} title="Hourly rate">
      <p className="px-card-pad pb-3 text-[13px] text-text-secondary text-center">
        Used by Pay Yourself First to project savings.
      </p>
      <NumPad
        initialValue={current}
        onDone={(v) => {
          if (v <= 0) return;
          onSave(v);
          onClose();
        }}
        doneLabel="Save"
      />
    </BottomSheet>
  );
}

// ---------- Edit pay cycle ----------

const CYCLES: Array<{
  value: PayCycle;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  { value: 'weekly', label: 'Weekly', description: 'Every 7 days', icon: CalendarDays },
  { value: 'biweekly', label: 'Biweekly', description: 'Every 2 weeks', icon: CalendarRange },
  { value: 'semimonthly', label: 'Semi-monthly', description: 'Twice a month', icon: CalendarClock },
  { value: 'monthly', label: 'Monthly', description: 'Once a month', icon: Calendar },
];

const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dayOfMonthOptions: DropdownOption[] = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: ordinal(i + 1),
}));

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function EditPayCycleSheet({
  open,
  profile,
  onClose,
  onSave,
}: {
  open: boolean;
  profile: Profile;
  onClose: () => void;
  onSave: (patch: Partial<Profile>) => void;
}) {
  const [cycle, setCycle] = useState<PayCycle>(profile.payCycle);
  const [payDay, setPayDay] = useState(profile.payDay);
  const [payDay2, setPayDay2] = useState(profile.payDay2 ?? 30);
  const [payAnchor, setPayAnchor] = useState(profile.payAnchor ?? '');

  useEffect(() => {
    if (!open) return;
    setCycle(profile.payCycle);
    setPayDay(profile.payDay);
    setPayDay2(profile.payDay2 ?? 30);
    setPayAnchor(profile.payAnchor ?? '');
  }, [open, profile]);

  // Reconcile cycle-specific defaults when the user changes cycle.
  const changeCycle = (next: PayCycle) => {
    setCycle(next);
    switch (next) {
      case 'weekly':
        setPayDay(5);
        break;
      case 'biweekly':
        // payAnchor stays — user picks below
        break;
      case 'semimonthly':
        setPayDay(15);
        setPayDay2(30);
        break;
      case 'monthly':
        setPayDay(1);
        break;
    }
  };

  const canSave = (() => {
    if (cycle === 'weekly') return payDay >= 0 && payDay <= 6;
    if (cycle === 'biweekly') return !!payAnchor;
    if (cycle === 'semimonthly') return payDay !== payDay2;
    if (cycle === 'monthly') return payDay >= 1 && payDay <= 31;
    return false;
  })();

  const submit = () => {
    if (!canSave) return;
    onSave({
      payCycle: cycle,
      payDay,
      payDay2: cycle === 'semimonthly' ? payDay2 : undefined,
      payAnchor: cycle === 'biweekly' ? payAnchor : undefined,
    });
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Pay cycle">
      <div className="px-card-pad py-4 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-card-gap">
          {CYCLES.map((c) => {
            const Icon = c.icon;
            const active = cycle === c.value;
            return (
              <motion.button
                key={c.value}
                type="button"
                onClick={() => changeCycle(c.value)}
                whileTap={tapScale}
                transition={springQuick}
                aria-pressed={active}
                className={cn(
                  'rounded-card p-3 text-left flex flex-col items-start gap-2',
                  active ? 'bg-accent/10' : 'bg-surface-secondary',
                )}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-button inline-flex items-center justify-center',
                    active ? 'bg-accent text-white' : 'bg-surface text-text-secondary',
                  )}
                >
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[14px] font-semibold text-text-primary">
                    {c.label}
                  </div>
                  <div className="text-[11px] text-text-secondary">
                    {c.description}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {cycle === 'weekly' ? (
          <div>
            <span className="text-caption uppercase text-text-tertiary mb-2 block">
              Day of the week
            </span>
            <div role="radiogroup" className="grid grid-cols-7 gap-2">
              {DOW_LABELS.map((letter, i) => {
                const active = payDay === i;
                return (
                  <motion.button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={DOW_FULL[i]}
                    onClick={() => setPayDay(i)}
                    whileTap={tapScale}
                    transition={springQuick}
                    className={cn(
                      'h-11 rounded-button text-[15px] font-medium',
                      active
                        ? 'bg-accent text-white shadow-md'
                        : 'bg-surface-secondary text-text-primary',
                    )}
                  >
                    {letter}
                  </motion.button>
                );
              })}
            </div>
          </div>
        ) : null}

        {cycle === 'biweekly' ? (
          <label className="block">
            <span className="text-caption uppercase text-text-tertiary mb-2 block">
              Most recent payday
            </span>
            <input
              type="date"
              value={payAnchor}
              onChange={(e) => setPayAnchor(e.target.value)}
              className={cn(
                'w-full h-12 px-4 rounded-button bg-surface-secondary',
                'text-[16px] text-text-primary',
                'focus:outline-none focus:ring-2 focus:ring-accent/40',
              )}
            />
          </label>
        ) : null}

        {cycle === 'semimonthly' ? (
          <div className="grid grid-cols-2 gap-card-gap">
            <label className="block">
              <span className="text-caption uppercase text-text-tertiary mb-2 block">
                First
              </span>
              <DropdownSelect
                options={dayOfMonthOptions}
                value={String(payDay)}
                onChange={(v) => setPayDay(Number(v))}
              />
            </label>
            <label className="block">
              <span className="text-caption uppercase text-text-tertiary mb-2 block">
                Second
              </span>
              <DropdownSelect
                options={dayOfMonthOptions}
                value={String(payDay2)}
                onChange={(v) => setPayDay2(Number(v))}
              />
            </label>
          </div>
        ) : null}

        {cycle === 'monthly' ? (
          <label className="block">
            <span className="text-caption uppercase text-text-tertiary mb-2 block">
              Day of the month
            </span>
            <DropdownSelect
              options={dayOfMonthOptions}
              value={String(payDay)}
              onChange={(v) => setPayDay(Number(v))}
            />
          </label>
        ) : null}

        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!canSave}
          onClick={submit}
        >
          Save
        </Button>
      </div>
    </BottomSheet>
  );
}

// ---------- Edit budget model ----------

const MODELS: Array<{
  value: BudgetModel;
  title: string;
  tagline: string;
  icon: LucideIcon;
}> = [
  { value: 'daily', title: 'Daily Allowance', tagline: 'Know what you can spend today', icon: Calendar },
  { value: '503020', title: '50 / 30 / 20', tagline: 'Balance needs, wants, and savings', icon: PieChart },
  { value: 'pyf', title: 'Pay Yourself First', tagline: 'Save first, spend what’s left', icon: PiggyBank },
  { value: 'zero', title: 'Zero-Based', tagline: 'Give every dollar a job', icon: Target },
  { value: 'conscious', title: 'Conscious Spending', tagline: 'Spend on what matters', icon: Compass },
];

export function EditBudgetModelSheet({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: BudgetModel;
  onClose: () => void;
  onSave: (model: BudgetModel) => void;
}) {
  const [model, setModel] = useState<BudgetModel>(current);

  useEffect(() => {
    if (open) setModel(current);
  }, [open, current]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Budget model">
      <div className="px-card-pad py-4 flex flex-col gap-card-gap">
        {MODELS.map((m) => {
          const Icon = m.icon;
          const active = model === m.value;
          return (
            <motion.button
              key={m.value}
              type="button"
              onClick={() => setModel(m.value)}
              whileTap={tapScale}
              transition={springQuick}
              aria-pressed={active}
              className={cn(
                'rounded-card p-card-pad flex items-center gap-3 text-left',
                active ? 'bg-accent/10' : 'bg-surface-secondary',
              )}
            >
              <div
                className={cn(
                  'w-11 h-11 rounded-button shrink-0 inline-flex items-center justify-center',
                  active ? 'bg-accent text-white' : 'bg-surface text-text-secondary',
                )}
              >
                <Icon size={22} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-text-primary">
                  {m.title}
                </div>
                <div className="text-[13px] text-text-secondary mt-0.5 leading-snug">
                  {m.tagline}
                </div>
              </div>
            </motion.button>
          );
        })}

        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => {
            onSave(model);
            onClose();
          }}
        >
          Save
        </Button>
      </div>
    </BottomSheet>
  );
}

// ---------- Edit rollover ----------

type RolloverOption = {
  value: 'continuous' | 'fresh';
  label: string;
  description: string;
};

const ROLLOVER_OPTIONS: RolloverOption[] = [
  {
    value: 'continuous',
    label: 'Continuous',
    description:
      "Unspent money carries forward. If you ended last period $200 ahead, your new period starts with that $200 plus this period's income. Saving builds momentum.",
  },
  {
    value: 'fresh',
    label: 'Fresh start',
    description:
      "Every period resets to zero plus this period's income. No carryover. Best if you treat each paycheck as a clean slate.",
  },
];

export function EditRolloverSheet({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: 'continuous' | 'fresh';
  onClose: () => void;
  onSave: (rollover: 'continuous' | 'fresh') => void;
}) {
  const [selected, setSelected] = useState<'continuous' | 'fresh'>(current);

  useEffect(() => {
    if (open) setSelected(current);
  }, [open, current]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Rollover balance">
      <p className="px-card-pad pb-3 text-[13px] text-text-secondary text-center leading-relaxed">
        How should unspent budget at the end of a period be handled?
      </p>
      <div className="px-card-pad pb-4 flex flex-col gap-2">
        {ROLLOVER_OPTIONS.map((opt) => {
          const active = opt.value === selected;
          return (
            <motion.button
              key={opt.value}
              type="button"
              onClick={() => setSelected(opt.value)}
              whileTap={tapScale}
              transition={springQuick}
              className={cn(
                'w-full text-left rounded-card border px-4 py-3',
                active
                  ? 'border-accent bg-accent/[0.06]'
                  : 'border-surface-secondary bg-surface',
              )}
            >
              <div
                className={cn(
                  'text-[15px] font-semibold',
                  active ? 'text-accent' : 'text-text-primary',
                )}
              >
                {opt.label}
              </div>
              <p className="mt-1 text-[13px] text-text-secondary leading-snug">
                {opt.description}
              </p>
            </motion.button>
          );
        })}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className="mt-2"
          onClick={() => {
            onSave(selected);
            onClose();
          }}
        >
          Save
        </Button>
      </div>
    </BottomSheet>
  );
}

// ---------- Edit work schedule (PYF) ----------

type WorkScheduleOption = {
  value: number;
  label: string;
  description: string;
};

const WORK_SCHEDULE_OPTIONS: WorkScheduleOption[] = [
  {
    value: 5,
    label: '5 days/week',
    description: 'Mon–Fri. The default. 260 workdays/yr.',
  },
  {
    value: 4,
    label: '4 days/week',
    description: '4×10s and similar. 208 workdays/yr.',
  },
  {
    value: 3,
    label: '3 days/week',
    description: '3×12s, part-time. 156 workdays/yr.',
  },
  {
    value: 6,
    label: '6 days/week',
    description: 'Six-day grind. 312 workdays/yr.',
  },
  {
    value: 7,
    label: '7 days/week',
    description: 'No days off. 365 workdays/yr.',
  },
];

export function EditWorkScheduleSheet({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: number;
  onClose: () => void;
  onSave: (workDaysPerWeek: number) => void;
}) {
  const [selected, setSelected] = useState<number>(current);

  useEffect(() => {
    if (open) setSelected(current);
  }, [open, current]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Work schedule">
      <p className="px-card-pad pb-3 text-[13px] text-text-secondary text-center leading-relaxed">
        How many days a week do you work? Pay-Yourself-First reserves
        one hour of pay per workday and projects savings from there.
      </p>
      <div className="px-card-pad pb-4 flex flex-col gap-2">
        {WORK_SCHEDULE_OPTIONS.map((opt) => {
          const active = opt.value === selected;
          return (
            <motion.button
              key={opt.value}
              type="button"
              onClick={() => setSelected(opt.value)}
              whileTap={tapScale}
              transition={springQuick}
              className={cn(
                'w-full text-left rounded-card border px-4 py-3',
                active
                  ? 'border-accent bg-accent/[0.06]'
                  : 'border-surface-secondary bg-surface',
              )}
            >
              <div
                className={cn(
                  'text-[15px] font-semibold',
                  active ? 'text-accent' : 'text-text-primary',
                )}
              >
                {opt.label}
              </div>
              <p className="mt-1 text-[13px] text-text-secondary leading-snug">
                {opt.description}
              </p>
            </motion.button>
          );
        })}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className="mt-2"
          onClick={() => {
            onSave(selected);
            onClose();
          }}
        >
          Save
        </Button>
      </div>
    </BottomSheet>
  );
}
